import React, { useEffect, useState } from 'react';
import { PlaybackTimingController } from './PlaybackTimingController';
import { usePlayerStore } from './playerStore';

interface PerformanceMetrics {
  drift: number;
  frameDrops: number;
  audioTime: number;
  canvasTime: number;
  isPlaying: boolean;
  eventProcessingRate: number;
  memoryUsage?: number;
  pointDensityInfo?: {
    totalStrokes: number;
    penStrokes: number;
    avgPointsPerStroke: number;
    avgPointsPerPenStroke: number;
  };
}

export const PerformanceMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    drift: 0,
    frameDrops: 0,
    audioTime: 0,
    canvasTime: 0,
    isPlaying: false,
    eventProcessingRate: 0
  });
  
  const [showMetrics, setShowMetrics] = useState(false);
  const { isPlaying, events } = usePlayerStore();
  const timingController = PlaybackTimingController.getInstance();
  
  useEffect(() => {
    if (!isPlaying) return;
    
    const updateMetrics = () => {
      const diagnostics = timingController.getDiagnostics();
      
      setMetrics({
        ...diagnostics,
        eventProcessingRate: calculateEventProcessingRate(),
        memoryUsage: getMemoryUsage(),
        pointDensityInfo: calculatePointDensity()
      });
    };
    
    const interval = setInterval(updateMetrics, 1000);
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  const calculateEventProcessingRate = (): number => {
    // This would track events processed per second
    // Implementation would require tracking in ActionProcessor
    return 0;
  };
  
  const getMemoryUsage = (): number | undefined => {
    // Check if performance.memory is available
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      return memInfo.usedJSHeapSize / 1048576; // Convert to MB
    }
    return undefined;
  };
  
  const calculatePointDensity = () => {
    const strokes: {[key: string]: { points: any[], tool: string }} = {};
    
    // Group stroke events by strokeId
    events.forEach(event => {
      if (event.type === 'add_stroke' && event.data?.stroke) {
        const stroke = event.data.stroke;
        strokes[stroke.id] = {
          points: stroke.points || [],
          tool: stroke.tool
        };
      }
    });
    
    const strokesArray = Object.values(strokes);
    const penStrokes = strokesArray.filter(s => s.tool === 'pen');
    
    const avgPointsPerStroke = strokesArray.length > 0 
      ? strokesArray.reduce((sum, s) => sum + s.points.length, 0) / strokesArray.length 
      : 0;
      
    const avgPointsPerPenStroke = penStrokes.length > 0
      ? penStrokes.reduce((sum, s) => sum + s.points.length, 0) / penStrokes.length
      : 0;
    
    return {
      totalStrokes: strokesArray.length,
      penStrokes: penStrokes.length,
      avgPointsPerStroke: Math.round(avgPointsPerStroke * 10) / 10,
      avgPointsPerPenStroke: Math.round(avgPointsPerPenStroke * 10) / 10
    };
  };
  
  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }
  
  // Keyboard shortcut to toggle metrics
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        setShowMetrics(!showMetrics);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showMetrics]);
  
  if (!showMetrics) {
    return null;
  }
  
  return (
    <div className="fixed top-20 right-4 bg-black/80 text-white p-4 rounded-lg text-xs font-mono z-50">
      <div className="mb-2 font-bold">Performance Metrics</div>
      <div>Audio Time: {(metrics.audioTime / 1000).toFixed(2)}s</div>
      <div>Canvas Time: {(metrics.canvasTime / 1000).toFixed(2)}s</div>
      <div className={`${metrics.drift > 50 ? 'text-red-400' : 'text-green-400'}`}>
        Drift: {metrics.drift.toFixed(0)}ms
      </div>
      <div className={`${metrics.frameDrops > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
        Frame Drops: {metrics.frameDrops}
      </div>
      {metrics.memoryUsage && (
        <div>Memory: {metrics.memoryUsage.toFixed(1)}MB</div>
      )}
      {metrics.pointDensityInfo && (
        <div className="mt-2 border-t border-gray-600 pt-2">
          <div className="text-yellow-400 font-bold">Point Density</div>
          <div>Total Strokes: {metrics.pointDensityInfo.totalStrokes}</div>
          <div>Pen Strokes: {metrics.pointDensityInfo.penStrokes}</div>
          <div>Avg Points/Stroke: {metrics.pointDensityInfo.avgPointsPerStroke}</div>
          <div className="text-cyan-400">Avg Points/Pen: {metrics.pointDensityInfo.avgPointsPerPenStroke}</div>
        </div>
      )}
      <div className="mt-2 text-gray-400">
        Press Ctrl+Shift+M to toggle
      </div>
    </div>
  );
};